import React, { useState, useRef } from 'react';

import Carousel from './Article/Carousel';
import Button from './Button';
import PageBuilderImage from './Article/PageBuilderImage';
import Editor from './Article/Editor';
import { StrapiMediaInfo } from 'apolloClient/types';

import Image from 'next/legacy/image';
import classnames from 'classnames';
import {
  hasHorizontalMargin,
  withHorizontalMarginWidth,
} from 'src/utils/pageBuilderMargin';
import rightArrow from '../public/images/arrow-right.svg';

export interface StylesType {
  [key: string]: string;
}

export type PageBuilderImageType = StrapiMediaInfo;

export interface PageBuilderElement {
  type: string | { resolvedName: string };
  nodes: string[];
  props: {
    styles: StylesType;
    image: PageBuilderImageType[];
    href: string;
    name: string;
    text: string;
    caption: string;
    captionLink?: string;
    images: PageBuilderImageType[];
  };
}

interface PageBuilderDataInterface {
  [key: string]: PageBuilderElement;
}

export interface PageBuilderParserInterface {
  data: { [key: string]: PageBuilderElement } | null;
  readMore?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  validElementsCount?: number;
}

export const parsePageBuilderData = (data: string) => {
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Data is not a JSON string:', data);
      console.error('Error parsing JSON in parsePageBuilderData', data);
      return null;
    }
  }
  return null;
};

interface NodeType {
  node: PageBuilderElement;
  data: PageBuilderDataInterface;
  readMore?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  validElementsCount?: number;
}

const Node: React.FC<NodeType> = ({
  node,
  data,
  readMore,
  containerRef,
  validElementsCount,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!node) return null;

  let typeName = '';
  if (typeof node.type === 'object') {
    typeName = node.type.resolvedName;
  } else {
    typeName = node.type;
  }

  const styles = node.props.styles;
  const childrenNodes = node.nodes.map((x: string) => (
    <Node key={x} node={data[x]} data={data} />
  ));

  const shouldUseReadMore = typeName === 'Container' && readMore;
  const visibleChildren =
    shouldUseReadMore && !expanded ? childrenNodes.slice(0, 3) : childrenNodes;

  const handleToggle = () => {
    if (expanded && containerRef?.current) {
      const top =
        containerRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: top - 80,
        behavior: 'smooth',
      });
    }
    setExpanded((prev) => !prev);
  };

  switch (typeName) {
    case 'Container':
      return (
        <div
          className="page-builder-container"
          data-pb-hmargin={hasHorizontalMargin(styles.margin) || undefined}
          style={withHorizontalMarginWidth(styles)}
        >
          {visibleChildren}
          {shouldUseReadMore && validElementsCount && validElementsCount > 3 && (
            <button
              className="flex items-start mt-4 lg:mt-[18px] lg:mb-[25px] mb-6 uppercase border-b-2 cursor-pointer border-gold md:mb-10 w-max"
              style={{ marginTop: '16px' }}
              onClick={handleToggle}
            >
              <div className="pt-1">
                <Image
                  className={classnames({
                    'transform -rotate-90': expanded,
                  })}
                  src={rightArrow}
                  alt="toggle Read More"
                  height={16}
                  width={16}
                />
              </div>
              <span className="ml-1 whitespace-nowrap">
                {expanded ? 'Hide' : 'Read more'}
              </span>
            </button>
          )}
        </div>
      );

    case 'Carousel':
      return <Carousel {...node.props} />;

    case 'Button':
      return (
        <Button
          classNames="page-builder-button"
          fullWidth={false}
          {...node.props}
        />
      );

    case 'Image':
      return (
        <PageBuilderImage
          styles={{ ...styles, color: styles.textColor }}
          image={node.props.image[0]}
          caption={node.props.caption}
          captionLink={node.props.captionLink}
        />
      );

    case 'Editor':
      return (
        <Editor
          styles={{ ...styles, maxWidth: '100%' }}
          text={node.props.text}
        />
      );

    default:
      return null;
  }
};

const PageBuilderContentParser: React.FC<PageBuilderParserInterface> = ({
  data,
  readMore = false,
  containerRef,
  validElementsCount,
}) => {
  if (!data) return <div>No blog content data</div>;
  return (
    <div className="flex flex-col page-builder-wrapper">
      <Node
        node={data.ROOT}
        data={data}
        readMore={readMore}
        containerRef={containerRef}
        validElementsCount={validElementsCount}
      />
    </div>
  );
};

export default PageBuilderContentParser;
